import * as React from "react";
import Alert, { AlertColor } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ReactMarkdown from "react-markdown";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { SystemAnnouncementLevel, SystemAnnouncementService } from "gen/clients/llts";

const markdownComponents = {
  a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  )
};

const severityByLevel: Record<SystemAnnouncementLevel, AlertColor> = {
  [SystemAnnouncementLevel.INFO]: "info",
  [SystemAnnouncementLevel.WARNING]: "warning",
  [SystemAnnouncementLevel.ERROR]: "error",
  [SystemAnnouncementLevel.SUCCESS]: "success"
};

const SystemAnnouncementBanner: React.FC = () => {
  const { t } = useTranslation();
  const { data: systemAnnouncement } = useQuery("getSystemAnnouncement", {
    queryFn: SystemAnnouncementService.getSystemAnnouncement,
    refetchInterval: 15 * 60 * 1000
  });

  const message = systemAnnouncement?.message?.trim();
  if (!systemAnnouncement?.isSystemAnnouncementEnabled || !message) {
    return null;
  }

  const severity = severityByLevel[systemAnnouncement.level || SystemAnnouncementLevel.WARNING];

  return (
    <Alert
      severity={severity}
      square={true}
      role="status"
      aria-live="polite"
      aria-label={t("components.systemAnnouncementBanner.ariaLabel")}
      icon={false}
      sx={{ "& .MuiAlert-message": { flex: 1, textAlign: "center" } }}
    >
      <Box
        sx={{
          fontSize: "1.005rem",
          "& p": { m: 0 },
          "& p + p": { mt: 1 },
          "& ul, & ol": { m: 0, pl: 0, listStylePosition: "inside" }
        }}
      >
        <ReactMarkdown components={markdownComponents}>{message}</ReactMarkdown>
      </Box>
    </Alert>
  );
};

export { SystemAnnouncementBanner };
